import { useEffect } from "react";
import { useRouter } from "@tanstack/react-router";

/**
 * Site-wide SPA navigation.
 *
 * Most navigation on the site (navbar, footer, cards, in-content links) is
 * written as plain <a href="/en/..."> markup, which causes a full page reload.
 * Instead of rewriting hundreds of anchors, we intercept same-origin anchor
 * clicks once, at the document level, and hand them to the router so the
 * transition happens client-side.
 *
 * An anchor opts out of this by adding `data-no-spa` (useful for links that
 * must hit the server, e.g. downloads or auth redirects).
 */
const SKIP_PREFIXES = ["/api/", "/sitemap", "/rss"];

const shouldSkip = (url: URL, anchor: HTMLAnchorElement): boolean => {
  if (anchor.dataset.noSpa !== undefined) return true;
  if (anchor.target && anchor.target !== "_self") return true;
  if (anchor.hasAttribute("download")) return true;
  const rel = anchor.getAttribute("rel") || "";
  if (/\bexternal\b/i.test(rel)) return true;
  if (url.origin !== window.location.origin) return true;
  if (SKIP_PREFIXES.some((p) => url.pathname.startsWith(p))) return true;
  // Anything that looks like a static file (e.g. /brochure.pdf).
  if (/\.[a-z0-9]{2,5}$/i.test(url.pathname)) return true;
  // Pure in-page anchors keep native behaviour (smooth scroll / :target).
  if (url.pathname === window.location.pathname && url.hash) return true;
  return false;
};

export default function SpaLinkInterceptor() {
  const router = useRouter();

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as Element | null;
      const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("#")) return;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (shouldSkip(url, anchor)) return;

      event.preventDefault();
      void router.navigate({
        href: `${url.pathname}${url.search}${url.hash}`,
        resetScroll: !url.hash,
      });
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [router]);

  return null;
}
